import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ShareModalShell.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Rendered inside the radial-gradient image region, above the gradient */
  imageRegion: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  /** The buttons; laid out by .trade-share-actions */
  actions: React.ReactNode;
  /** Extra class on the modal surface, for a payload that needs its own region sizing */
  className?: string;
}

/**
 * The share modal's chrome and nothing else: backdrop, #120d1e surface with the
 * 0.5px #5a3fa0 border and 20px radius, the radial-gradient image region, the
 * title/subtitle block and the actions row. Payloads supply the slots. The
 * completed-trade share (TradeShareModal) and the trade-map share both render
 * through it, so the two can never drift apart. Class names are unchanged from
 * the original TradeShareModal.
 */
const ShareModalShell: React.FC<Props> = ({ isOpen, onClose, imageRegion, title, subtitle, actions, className }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className="trade-share-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trade-share-title"
    >
      <div className={className ? `trade-share-modal ${className}` : 'trade-share-modal'}>
        <div className="trade-share-image-region">
          <div className="trade-share-gradient" />
          {imageRegion}
        </div>

        <div className="trade-share-content">
          <p id="trade-share-title" className="trade-share-title">
            {title}
          </p>
          <p className="trade-share-subtitle">{subtitle}</p>
          <div className="trade-share-actions">{actions}</div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareModalShell;
